import invariant from "tiny-invariant";

import { GitRepositoryProvider } from "../client/generated/data-contracts";
import api from "./api";

type QueryFn<T> = () => Promise<T>;
type Querykey = (string | number | boolean | undefined)[];

type Query<T> = {
  queryKey: Querykey;
  queryFn: QueryFn<T>;
};

type QueryCreator<T> = (...args: any[]) => Query<T>;

// Allow nesting of query maps
//
// Example:
//
// const queries = factory({
//   tenant: {
//     get: (id: string) => ({
//       queryKey: [id],
//       queryFn: async () => (await api.browser.tenantGet(id)).data,
//     }),
//   },
// });
type QueryMap = {
  [key: string]: Query<any> | QueryCreator<any> | QueryMap;
};

// Make the factory function generic so that we can type the return value
type QueryMapFactory = <T extends QueryMap>(obj: T) => T;

const factory: QueryMapFactory = (obj) => {
  // Recursive function to build query keys
  const buildQueryKey = (path: string[], value: any): any => {
    // Case 1: Value is a QueryCreator function
    if (typeof value === "function") {
      // Return a new function that modifies the query key
      return (...args: any[]) => {
        const query = value(...args);
        return {
          ...query,
          queryKey: [
            ...path,
            ...(Array.isArray(query.queryKey)
              ? query.queryKey
              : query.queryKey !== null
                ? [query.queryKey]
                : []),
          ],
        };
      };
    }
    // Case 2: Value is an object (potentially a nested QueryMap or a Query object)
    else if (typeof value === "object" && value !== null) {
      // If the object has a 'queryFn' property, it's a Query object
      if (value.queryFn) {
        return {
          ...value,
          queryKey: [
            ...path,
            ...(Array.isArray(value.queryKey)
              ? value.queryKey
              : value.queryKey !== null
                ? [value.queryKey]
                : []),
          ],
        };
      }
      // Otherwise, process each key-value pair in the object
      return Object.keys(value).reduce((acc, key) => {
        acc[key] = buildQueryKey([...path, key], value[key]);
        return acc;
      }, {} as any);
    }
    // Case 3: Value is neither a function nor an object (leaf node)
    return value;
  };

  return buildQueryKey([], obj);
};

const preQueries = factory({
  meta: {
    get: {
      queryKey: [],
      queryFn: async () => (await api.browser.metaGet()).data,
    },
  },
  user: {
    current: {
      queryKey: [],
      queryFn: async () => (await api.browser.userGetCurrent()).data,
    },
    invites: {
      queryKey: [],
      queryFn: async () => (await api.browser.userGetInvites()).data.rows,
    },
    superadmins: {
      queryKey: [],
      queryFn: async () => (await api.browser.userGetSuperadmins()).data,
    },
    getExposedAccounts: () => ({
      queryKey: ["user:get:exposed-accounts"],
      queryFn: async () => (await api.browser.userGetExposedAccounts()).data,
    }),
  },
  tenant: {
    get: (id: string) => ({
      queryKey: [id],
      queryFn: async () => (await api.browser.tenantGet(id)).data,
    }),
    list: {
      queryKey: [],
      queryFn: async () =>
        (await api.browser.adminListTenants()).data.rows || [],
    },
    listInvites: (id: string) => ({
      queryKey: [id],
      queryFn: async () =>
        (await api.browser.tenantInviteList(id)).data.rows || [],
    }),
    getCodeHealthOverview: (tenantId?: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        return await api.browser.tenantGetCodeHealthOverview(tenantId);
      },
    }),
    getChsScanInfo: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetChsScanInfo(tenantId)).data,
    }),
    getCodeSecurityScanInfo: (
      tenantId: string,
      query?: {
        repositoryIds?: string[];
      },
    ) => ({
      queryKey: [
        "tenant:get:code-security-scan-info",
        tenantId,
        ...(query?.repositoryIds?.sort() ?? []),
      ],
      queryFn: async () =>
        (await api.browser.tenantGetCodeSecurityScanInfo(tenantId, query)).data,
    }),
    getCodeSecuritySettings: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetCodeSecuritySettings(tenantId)).data,
    }),
    getTrustPage: (id: string) => ({
      queryKey: [id],
      queryFn: async () =>
        (await api.browserWithoutCredentials.tenantGetTrust(id)).data,
    }),
    getSlackSettings: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetSlackSettings(tenantId)).data,
    }),
    getSlackChannels: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetSlackChannels(tenantId)).data,
    }),
    getExposedAccounts: (tenantId: string) => ({
      queryKey: ["tenant:get:exposed-accounts", tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetExposedAccounts(tenantId)).data,
    }),
  },
  tenantMember: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantMemberList(tenantId)).data.rows ?? [],
    }),
  },
  membership: {
    list: {
      queryKey: [],
      queryFn: async () => (await api.browser.membershipList()).data.rows || [],
    },
  },
  tenantFeatureFlag: {
    list: (tenantId?: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        return (
          (await api.browser.tenantFeatureFlagsList(tenantId)).data.rows ?? []
        );
      },
    }),
  },
  checklistTemplate: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.checklistTemplateList(tenantId)).data.rows ?? [],
    }),
  },
  memberChecklist: {
    list: (memberId: string) => ({
      queryKey: [memberId],
      queryFn: async () =>
        (await api.browser.memberChecklistList(memberId)).data.rows ?? [],
    }),
  },
  monitor: {
    list: (
      tenantId?: string,
      {
        assetTypeId,
        isEnabled,
        reviewRemindBefore,
      }: {
        assetTypeId?: string;
        isEnabled?: boolean;
        reviewRemindBefore?: string;
      } = {},
    ) => ({
      queryKey: [tenantId, assetTypeId, isEnabled, reviewRemindBefore],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        return (
          (
            await api.browser.monitorList(tenantId, {
              assetTypeId,
              isEnabled,
              reviewRemindBefore,
            })
          ).data.rows ?? []
        );
      },
    }),
    listByIntegration: (integrationId: string) => ({
      queryKey: [integrationId],
      queryFn: async () =>
        (await api.browser.integrationListMonitors(integrationId)).data.rows ??
        [],
    }),
    get: (monitorId: string) => ({
      queryKey: [monitorId],
      queryFn: async () => {
        const res = await api.browser.monitorGet(monitorId);

        return res.data;
      },
    }),
    listControls: (monitorId: string) => ({
      queryKey: [monitorId],
      queryFn: async () =>
        (await api.browser.monitorGetControls(monitorId)).data.rows ?? [],
    }),
  },
  control: {
    get: (controlId: string) => ({
      queryKey: [controlId],
      queryFn: async () => (await api.browser.controlGet(controlId)).data,
    }),
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.controlList(tenantId)).data.rows || [],
    }),
    listByTenantComplianceFramework: (tenantFrameworkId: string) => ({
      queryKey: [tenantFrameworkId],
      queryFn: async () =>
        (
          await api.browser.controlListTenantComplianceFramework(
            tenantFrameworkId,
          )
        ).data.rows ?? [],
    }),
    assignReviewer: (controlId: string, reviewerId: string) => ({
      queryKey: [controlId, reviewerId],
      queryFn: async () =>
        (
          await api.browser.controlUpdateAssignedReviewer(controlId, {
            reviewerId,
          })
        ).data,
    }),
    getActivities: (controlId: string) => ({
      queryKey: [controlId],
      queryFn: async () =>
        (await api.browser.controlGetActivities(controlId)).data ?? [],
    }),
  },
  evidence: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.evidenceList(tenantId)).data ?? [],
    }),
  },
  policy: {
    get: (id: string) => ({
      queryKey: [id],
      queryFn: async () => (await api.browser.policyGet(id)).data,
    }),
    getMe: (id: string) => ({
      queryKey: [id],
      queryFn: async () => (await api.browser.policyGetMe(id)).data,
    }),
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.policyList(tenantId)).data.rows ?? [],
    }),
    listMe: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.policyListMe(tenantId)).data ?? [],
    }),
  },
  policyType: {
    list: {
      queryKey: [],
      queryFn: async () => (await api.browser.policyTypeList()).data.rows ?? [],
    },
  },
  policyVersion: {
    get: (id?: string) => ({
      queryKey: [id],
      queryFn: async () => {
        invariant(id, "policy version id is required");
        return (await api.browser.policyVersionGet(id)).data;
      },
    }),
    list: (policyId: string) => ({
      queryKey: [policyId],
      queryFn: async () =>
        (await api.browser.policyVersionList(policyId)).data.rows ?? [],
    }),
  },
  policySignature: {
    list: (versionId?: string) => ({
      queryKey: [versionId],
      queryFn: async () => {
        invariant(versionId, "version id is required");
        return (
          (await api.browser.policySignatureList(versionId)).data.rows ?? []
        );
      },
    }),
  },
  integration: {
    list: (
      tenantId?: string,
      { includeOneleetManaged }: { includeOneleetManaged?: boolean } = {},
    ) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        let { rows: integrations, isPartial } = (
          await api.browser.integrationList(tenantId, {
            includeOneleetManaged: includeOneleetManaged || false,
          })
        ).data;
        integrations = (integrations ?? []).sort((a, b) => {
          let aName = a.integrationType?.name ?? "";
          let bName = b.integrationType?.name ?? "";
          return aName.localeCompare(bName);
        });
        return { integrations, isPartial };
      },
    }),
    get: (integrationId?: string) => ({
      queryKey: [integrationId],
      queryFn: async () => {
        invariant(
          integrationId,
          "integration id is required to fetch an integration",
        );
        return (await api.browser.integrationGet(integrationId)).data;
      },
    }),
  },
  integrationType: {
    list: (tenantId?: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        const { rows: integrationTypes, isPartial } = (
          await api.browser.integrationListTenantTypes(tenantId)
        ).data;
        return { integrationTypes: integrationTypes ?? [], isPartial };
      },
    }),
  },
  subdomain: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.subdomainList(tenantId)).data.rows || [],
    }),
  },
  workflow: {
    list: (
      ...[tenantId, query]: Parameters<typeof api.browser.workflowList>
    ) => ({
      queryKey: [tenantId, query?.type],
      queryFn: async () =>
        (await api.browser.workflowList(tenantId, query)).data.rows || [],
    }),
  },
  domain: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.domainList(tenantId)).data.rows || [],
    }),
  },
  scanFinding: {
    get: (findingId: string) => ({
      queryKey: [findingId],
      queryFn: async () => (await api.browser.scanFindingGet(findingId)).data,
    }),
    list: (
      ...[tenantId, query]: Parameters<typeof api.browser.scanFindingList>
    ) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.scanFindingList(tenantId, query)).data.rows ?? [],
    }),
  },
  finding: {
    get: (findingId: string) => ({
      queryKey: [findingId],
      queryFn: async () => (await api.browser.findingGet(findingId)).data,
    }),
  },
  // TODO: DEPRECATED; this is part of the old codehealthscanner
  codeHealthIssue: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.codeHealthIssueList(tenantId)).data ?? [],
    }),
  },
  // TODO: DEPRECATED; this is part of the old codehealthscanner
  codeHealthIssueType: {
    list: () => ({
      queryKey: [],
      queryFn: async () =>
        (await api.browser.codeHealthIssueTypeList()).data || [],
    }),
  },
  // TODO: DEPRECATED; this is part of the old codehealthscanner
  codeHealthIgnoreRule: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.codeHealthIgnoreRuleList(tenantId)).data || [],
    }),
  },
  gitRepository: {
    get: (gitRepositoryId?: string) => ({
      queryKey: [gitRepositoryId],
      queryFn: async () => {
        invariant(gitRepositoryId, "git repository id is required");
        return (await api.browser.gitRepositoryGet(gitRepositoryId)).data;
      },
    }),
    list: (tenantId?: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        return (await api.browser.gitRepositoryList(tenantId)).data ?? [];
      },
    }),
    listAvailable: (tenantId?: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        return (
          (await api.browser.gitRepositoryListAvailable(tenantId)).data ?? []
        );
      },
    }),
    listBranches: (
      tenantId: string,
      provider: GitRepositoryProvider,
      owner: string,
      name: string,
    ) => ({
      queryKey: [tenantId, owner, name],
      queryFn: async () => {
        // URL-encode the name parameter to handle repository names with slashes
        const encodedName = encodeURIComponent(name);
        return (
          (
            await api.browser.gitRepositoryListBranches(
              tenantId,
              provider,
              owner,
              encodedName,
            )
          ).data ?? []
        );
      },
    }),
  },
  codeSecurityFinding: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.codeSecurityFindingList(tenantId)).data ?? [],
    }),
  },
  codeSecurityIgnoreRule: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.codeSecurityIgnoreRuleList(tenantId)).data ?? [],
    }),
  },
  task: {
    get: (taskId: string) => ({
      queryKey: [taskId],
      queryFn: async () => (await api.browser.taskGet(taskId)).data,
    }),
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.taskList(tenantId)).data.rows || [];
      },
    }),
  },
  complianceFramework: {
    list: (tenantId?: string) => ({
      queryKey: [],
      queryFn: async () => {
        return (
          (
            await api.browser.complianceFrameworkList(
              tenantId ? { tenant: tenantId } : undefined,
            )
          ).data.rows || []
        );
      },
    }),
  },
  tenantComplianceFramework: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (
          (await api.browser.tenantComplianceFrameworkList(tenantId)).data
            .rows ?? []
        );
      },
    }),
    getProgramData: (tenantId: string) => ({
      queryKey: [tenantId, "program-data"],
      queryFn: async () => {
        return (await api.browser.tenantGetComplianceProgram(tenantId)).data;
      },
    }),
    listAdmin: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (
          (await api.browser.tenantComplianceFrameworkListAdmin(tenantId)).data
            .rows ?? []
        );
      },
    }),
    listEmployee: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (
          (await api.browser.tenantComplianceFrameworkListEmployee(tenantId))
            .data.rows ?? []
        );
      },
    }),
  },
  tenantComplianceRequirement: {
    get: (requirementId: string) => ({
      queryKey: [requirementId],
      queryFn: async () => {
        return (await api.browser.tenantComplianceRequirementGet(requirementId))
          .data;
      },
    }),
    list: (frameworkId: string) => ({
      queryKey: [frameworkId],
      queryFn: async () => {
        return (
          (await api.browser.tenantComplianceRequirementList(frameworkId)).data
            .rows || []
        );
      },
    }),
  },
  tenantTrustConfigs: {
    get: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetTrustConfigs(tenantId)).data,
    }),
  },
  tenantTrustDocumentRequests: {
    get: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetTrustDocumentRequests(tenantId)).data,
    }),
  },
  tenantTrustSecurityIssueReports: {
    get: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () =>
        (await api.browser.tenantGetTrustSecurityIssues(tenantId)).data,
    }),
  },
  trustDocument: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => (await api.browser.trustDocumentList(tenantId)).data,
    }),
  },
  tenantDevice: {
    get: (tenantDeviceId: string) => ({
      queryKey: [tenantDeviceId],
      queryFn: async () => {
        return (await api.browser.tenantDeviceGet(tenantDeviceId)).data;
      },
    }),
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.tenantDeviceList(tenantId)).data.rows ?? [];
      },
    }),
    listMe: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.tenantDeviceListMe(tenantId)).data.rows ?? [];
      },
    }),
  },
  exception: {
    list: (...params: Parameters<typeof api.browser.exceptionList>) => {
      const [tenantId, query] = params;
      return {
        queryKey: [tenantId, ...Object.values(query ?? {})],
        queryFn: async () => {
          return (await api.browser.exceptionList(...params)).data ?? [];
        },
      };
    },
  },
  controlReview: {
    list: (...params: Parameters<typeof api.browser.controlListReviews>) => ({
      queryKey: [],
      queryFn: async () => {
        return (await api.browser.controlListReviews(...params)).data;
      },
    }),
  },
  externalAccount: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (
          (await api.browser.externalAccountList(tenantId)).data.rows ?? []
        );
      },
    }),
  },
  vendor: {
    list: {
      queryKey: ["vendor", "list"],
      queryFn: async () => {
        return (await api.browser.vendorList()).data.rows || [];
      },
    },
  },
  tenantVendor: {
    get: (...params: Parameters<typeof api.browser.tenantVendorGet>) => ({
      queryKey: ["tenant:vendor", "get", params[0] ?? ""],
      queryFn: async () => {
        return (await api.browser.tenantVendorGet(...params)).data;
      },
    }),
    discovery: (
      ...params: Parameters<typeof api.browser.tenantVendorDiscoveryList>
    ) => ({
      queryKey: ["tenant:vendor", "discovery"],
      queryFn: async () => {
        return (
          (await api.browser.tenantVendorDiscoveryList(...params)).data
            .discoveries || []
        );
      },
    }),
    list: (...params: Parameters<typeof api.browser.tenantVendorList>) => ({
      queryKey: ["tenant:vendor", "list", params[0] ?? ""],
      queryFn: async () => {
        return (await api.browser.tenantVendorList(...params)).data.rows || [];
      },
    }),
  },
  document: {
    list: (tenantId?: string) => {
      invariant(tenantId, "tenant id is required");
      return {
        queryKey: [tenantId],
        queryFn: async () => {
          return (await api.browser.documentList(tenantId)).data.rows || [];
        },
      };
    },

    get: (templateId: string) => ({
      queryKey: [templateId],
      queryFn: async () => {
        return (await api.browser.documentGet(templateId)).data;
      },
    }),
  },

  documentTemplate: {
    list: (tenantId?: string) => {
      invariant(tenantId, "tenant id is required");
      return {
        queryKey: [tenantId],
        queryFn: async () => {
          return (
            (await api.browser.documentTemplateList(tenantId)).data.rows || []
          );
        },
      };
    },
    get: (templateId?: string) => {
      invariant(templateId, "template id is required");
      return {
        queryKey: [templateId],
        queryFn: async () => {
          return (await api.browser.documentTemplateGet(templateId)).data;
        },
      };
    },
  },

  risk: {
    get: (riskId: string) => ({
      queryKey: [riskId],
      queryFn: async () => {
        return (await api.browser.riskGet(riskId)).data;
      },
    }),
  },

  riskAssessment: {
    get: (assessmentId: string) => ({
      queryKey: [assessmentId],
      queryFn: async () => {
        return (await api.browser.riskAssessmentGet(assessmentId)).data;
      },
    }),
    list: (
      tenantId: string,
      query?: { completed?: boolean; limit?: number },
    ) => ({
      queryKey: [tenantId, query?.completed, query?.limit],
      queryFn: async () => {
        return (
          (await api.browser.riskAssessmentList(tenantId, query)).data ?? []
        );
      },
    }),
  },

  report: {
    get: (reportId?: string, snapshot?: string) => ({
      queryKey: ["report:get", reportId, snapshot],
      queryFn: async () => {
        invariant(reportId, "report is required");

        return (
          await api.browser.reportGet(reportId, {
            snapshot,
          })
        ).data;
      },
    }),
    listVersions: (reportId?: string) => ({
      queryKey: ["report:list:versions", reportId],
      queryFn: async () => {
        invariant(reportId, "report is required");
        return (await api.browser.reportVersionList(reportId)).data.rows;
      },
    }),
    getDownload: (reportId?: string, version?: number) => ({
      queryKey: ["report:get:download", reportId, version],
      queryFn: async () => {
        invariant(reportId, "report id is required");
        invariant(version, "version is required");
        return (
          await api.browser.reportGetDownload(reportId, {
            version: version,
          })
        ).data;
      },
    }),
  },

  audit: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.auditList(tenantId)).data || [];
      },
    }),
    listAll: (stages: string[]) => ({
      queryKey: [...stages],
      queryFn: async () => {
        return (await api.browser.auditListAll({ stages })).data || [];
      },
    }),
  },

  auditType: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.auditTypeList(tenantId)).data || [];
      },
    }),
  },

  group: {
    get: (groupId: string) => ({
      queryKey: [groupId],
      queryFn: async () => {
        return (await api.browser.groupGet(groupId)).data;
      },
    }),
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.groupList(tenantId)).data || [];
      },
    }),
  },

  engagement: {
    get: (engagement?: string) => ({
      queryKey: [engagement],
      queryFn: async () => {
        invariant(engagement, "engagement is required");

        const res = await api.browser.engagementGet(engagement);

        return res;
      },
    }),
  },

  appleMdmSettings: {
    get: (tenantId: string) => ({
      queryKey: ["tenant:get:apple-mdm", tenantId],
      queryFn: async () => {
        return (await api.browser.tenantGetAppleMdm(tenantId)).data;
      },
    }),
  },
  accessReview: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        const res = await api.browser.accessReviewList(tenantId);
        return res.data;
      },
    }),
    get: (reviewId: string) => ({
      queryKey: [reviewId],
      queryFn: async () => {
        const res = await api.browser.accessReviewGet(reviewId);
        return res.data;
      },
    }),
    listAccessReviewAccounts: (tenantMemberId: string, reviewId: string) => ({
      queryKey: [tenantMemberId, reviewId],
      queryFn: async () => {
        const res = await api.browser.accessReviewAccountListTenantMember(
          reviewId,
          { tenantMemberId },
        );
        return res.data;
      },
    }),
  },
  pentestReports: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.tenantReportsList(tenantId)).data.rows || [];
      },
    }),
  },
  attackSurfaceAsset: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        const res = await api.browser.attackSurfaceAssetList(tenantId);
        return res.data;
      },
    }),
  },
  attackSurfaceTarget: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        const res = await api.browser.attackSurfaceTargetList(tenantId);
        return res.data;
      },
    }),
  },
  attackSurfaceScan: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        const res = await api.browser.tenantGetAttackSurfaceScanInfo(tenantId);
        return res.data;
      },
    }),
  },
  userSecurityTraining: {
    list: (tenantId: string, latest?: boolean) => ({
      queryKey: [tenantId, latest?.toString()] as const,
      queryFn: async () => {
        const response =
          await api.browser.securityTrainingModuleListMyProgress(tenantId);
        return response.data;
      },
    }),
  },
  securityTraining: {
    getModule: (moduleId: string) => ({
      queryKey: [moduleId],
      queryFn: async () => {
        const response = await api.browser.securityTrainingModuleGet(moduleId);
        return response.data;
      },
    }),
    list: (tenantId: string, { latest }: { latest?: boolean }) => ({
      queryKey: [tenantId, latest],
      queryFn: async () => {
        const response = await api.browser.securityTrainingModuleList(tenantId);
        return response.data;
      },
    }),
    listUserProgress: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        const response =
          await api.browser.securityTrainingModuleListUserProgress(tenantId);
        return response.data;
      },
    }),
    myProgress: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        const response =
          await api.browser.securityTrainingModuleListMyProgress(tenantId);
        return response.data;
      },
    }),
    updateMyProgress: (
      tenantId: string,
      moduleId: string,
      sectionId: string,
    ) => ({
      queryKey: [tenantId, moduleId, sectionId],
      queryFn: async () => {
        const response =
          await api.browser.securityTrainingModuleUpdateMyProgress(moduleId, {
            sectionId,
            isCompleted: true,
          });
        return response.data;
      },
    }),
  },
  assets: {
    snapshotList: (monitorId: string) => ({
      queryKey: [monitorId],
      queryFn: async () => {
        const res = await api.browser.monitorResultSnapshotList(monitorId);
        return res.data;
      },
      refetchInterval: 5000,
    }),
  },
  hmodules: {
    getMeta: (tenantId?: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        return (await api.browser.tenantGetHModulesMeta(tenantId)).data;
      },
    }),
    get: (tenantId?: string, hModuleId?: string) => ({
      queryKey: [tenantId, hModuleId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        invariant(hModuleId, "h module id is required");
        return (await api.browser.tenantGetHModuleLanding(tenantId, hModuleId))
          .data;
      },
    }),
    getItem: (
      tenantId?: string,
      hModuleId?: string,
      hmoduleItemId?: string,
    ) => ({
      queryKey: [tenantId, hModuleId, hmoduleItemId],
      queryFn: async () => {
        invariant(tenantId, "tenant id is required");
        invariant(hModuleId, "h module id is required");
        invariant(hmoduleItemId, "h module item id is required");
        return (
          await api.browser.tenantGetHModuleItem(
            tenantId,
            hModuleId,
            hmoduleItemId,
          )
        ).data;
      },
    }),
  },

  nango: {
    listIntegrations: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return null;
      },
    }),
  },

  questionnaireCenter: {
    list: (tenantId: string) => ({
      queryKey: [tenantId],
      queryFn: async () => {
        return (await api.browser.questionnaireList(tenantId)).data;
      },
    }),

    get: (questionnaireId: string) => ({
      queryKey: [questionnaireId],
      queryFn: async () => {
        return (await api.browser.questionnaireGet(questionnaireId)).data;
      },
    }),
  },
});

export const queries = preQueries;
